import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { errorMessage } from '@/api/common';
import { mediaNetworkApi } from '@/api/media-network/mediaNetworkApi';
import { mediaNetworkKeys } from '@/api/media-network/mediaNetworkQueries';
import type { Advertiser } from '@/models/media-network/Advertiser';
import { useLocalizedStrings } from '../../../localization/LocalizedStringsProvider';
import { useToast } from '../../common/toast/ToastProvider';
import ReadOnlyFieldset from '../../common/dialog/ReadOnlyFieldset';

interface Props {
    open: boolean;
    brandId: string;
    advertiser: Advertiser | null; // null = create
    // View-only: the grid opens this with an eye rather than a pencil for read access alone.
    readOnly?: boolean;
    onClose: () => void;
}

const AdvertiserDialog = ({ open, brandId, advertiser, readOnly, onClose }: Props) => {
    const qc = useQueryClient();
    const toast = useToast();
    const strings = useLocalizedStrings();

    const [name, setName] = useState('');
    const [externalId, setExternalId] = useState('');
    const [enabled, setEnabled] = useState(true);

    useEffect(() => {
        if (!open) return;
        setName(advertiser?.name ?? '');
        setExternalId(advertiser?.externalId ?? '');
        setEnabled(advertiser?.enabled ?? true);
    }, [open, advertiser]);

    const { mutateAsync: save, isPending } = useMutation({
        mutationFn: async () => {
            const body = {
                name: name.trim(),
                externalId: externalId.trim() || null,
                enabled,
            };

            if (advertiser) {
                await mediaNetworkApi.updateAdvertiser(advertiser.id, body);
            } else {
                await mediaNetworkApi.createAdvertiser({ ...body, brandId });
            }
        },
        onSuccess: async () => {
            toast({ severity: 'success', message: strings.playlistAdvertiserSaved });
            await qc.invalidateQueries({ queryKey: mediaNetworkKeys.advertisers(brandId) });
            // A rename changes what the media dialog's picker shows, and disabling changes which options
            // appear there — so the playlist list is stale too.
            await qc.invalidateQueries({ queryKey: mediaNetworkKeys.list(brandId) });
            onClose();
        },
        // The API rejects a duplicate name within the brand; its message names the clash.
        onError: (e: unknown) => toast({
            severity: 'error',
            message: errorMessage(e, strings.playlistAdvertiserSaveError),
        }),
    });

    const canSave = !!name.trim();

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{advertiser ? strings.playlistEditAdvertiser : strings.playlistNewAdvertiser}</DialogTitle>
            <DialogContent>
                <ReadOnlyFieldset readOnly={readOnly}>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        label={strings.name}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        fullWidth
                        autoFocus
                    />

                    <TextField
                        label={strings.playlistExternalId}
                        value={externalId}
                        onChange={(e) => setExternalId(e.target.value)}
                        helperText={strings.playlistExternalIdHelp}
                        fullWidth
                    />

                    {/* Disabling is the intended alternative to deleting: it hides the advertiser from the
                        pickers while past media keeps its attribution, so old reporting still resolves a name
                        rather than an orphaned id. */}
                    <FormControlLabel
                        control={<Checkbox checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
                        label={strings.enabled}
                    />
                </Stack>
            </ReadOnlyFieldset>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onClose}>{readOnly ? strings.close : strings.cancel}</Button>
                {!readOnly && <Button variant="contained" loading={isPending} disabled={!canSave || isPending} onClick={() => save()}>
                    {strings.save}
                </Button>}
            </DialogActions>
        </Dialog>
    );
};

export default AdvertiserDialog;
